// docs/22 O4/B2 — the opening's cinematics, client-only state shared by the HUD and the scene:
// where the board camera should look (the face-off frames the leader), which unit is the World's
// first named foe and how it gets away (the scene plays that instead of a fall), and the per-browser
// "seen" marks for the face-off and the escape card. The scene never reads server rows; the HUD
// publishes the named foe here from the story.
import { create } from 'zustand';
import type { Escape, Genesis } from '../story/genesis';
import type { Tile } from '../sim/types';

export interface NamedFoe { encounterId: bigint; name: string; unitId?: number; escape: Escape }

export interface CinematicStore {
  /** The tile the board camera frames (the face-off); undefined = the player's own camera. */
  focus?: Tile;
  /** How much closer than the player's zoom the frame goes (the face-off 1.4×; a leader's call, docs/91, closer). */
  focusCloser?: number;
  setFocus(t?: Tile, closer?: number): void;
  namedFoe?: NamedFoe;
  setNamedFoe(n?: NamedFoe): void;
  /** docs/50: the opening's story, for the scene to cue its battle beats from. */
  story?: Genesis;
  setStory(g?: Genesis): void;
  /** The encounter whose named foe got away on the board (set by the scene as the effect plays). */
  escaped?: bigint;
  markEscaped(encounterId: bigint): void;
  /** True while the face-off (or its objective card) is on screen; other cards over the board wait (docs/29). */
  faceoffPlaying: boolean;
  setFaceoffPlaying(on: boolean): void;
  rev: number;
  faceoffSeen(encounterId: bigint): boolean;
  setFaceoffSeen(encounterId: bigint): void;
  escapeSeen(encounterId: bigint): boolean;
  setEscapeSeen(encounterId: bigint): void;
  /** docs/77 NF3: the "Foe Defeated!" card has been seen for this encounter. */
  felledSeen(encounterId: bigint): boolean;
  setFelledSeen(encounterId: bigint): void;
  /**
   * docs/56 EP5: the party's last hero fell on the board — the defeat card holds the fight's end
   * (and the Spoils screen) until the player continues.
   */
  defeat?: { encounterId: bigint; hero: string; continued: boolean };
  setDefeat(encounterId: bigint, hero: string): void;
  continueDefeat(): void;
}

const get = (k: string) => { try { return localStorage.getItem(k) === '1'; } catch { return false; } };
const put = (k: string) => { try { localStorage.setItem(k, '1'); } catch { /* private mode */ } };

export const useCinematic = create<CinematicStore>()((set, getState) => ({
  rev: 0,
  faceoffPlaying: false,
  setFaceoffPlaying(faceoffPlaying) { if (getState().faceoffPlaying !== faceoffPlaying) set({ faceoffPlaying }); },
  setFocus(focus, focusCloser) { const f = getState().focus; if (f?.x !== focus?.x || f?.y !== focus?.y) set({ focus, focusCloser: focus ? focusCloser : undefined }); },
  setStory(story) { if (getState().story !== story) set({ story }); },
  setNamedFoe(namedFoe) { const n = getState().namedFoe; if (n?.encounterId !== namedFoe?.encounterId || n?.name !== namedFoe?.name || n?.unitId !== namedFoe?.unitId || n?.escape !== namedFoe?.escape) set({ namedFoe }); },
  markEscaped(escaped) { set({ escaped }); },
  faceoffSeen: (e) => get(`fk.faceoff.${e}`),
  setFaceoffSeen(e) { put(`fk.faceoff.${e}`); set({ rev: getState().rev + 1 }); },
  escapeSeen: (e) => get(`fk.escape.${e}`),
  setEscapeSeen(e) { put(`fk.escape.${e}`); set({ rev: getState().rev + 1 }); },
  felledSeen: (e) => get(`fk.felled.${e}`),
  setFelledSeen(e) { put(`fk.felled.${e}`); set({ rev: getState().rev + 1 }); },
  setDefeat(encounterId, hero) { if (getState().defeat?.encounterId !== encounterId) set({ defeat: { encounterId, hero, continued: false } }); },
  continueDefeat() { const d = getState().defeat; if (d && !d.continued) set({ defeat: { ...d, continued: true } }); },
}));

/** Is `unit` the named foe of the encounter on the board? (by unit id when known, else by the story's name) */
export function isNamedFoe(n: NamedFoe | undefined, encounterId: bigint | undefined, unit: { unitId: number; name?: string }): boolean {
  if (!n || encounterId === undefined || n.encounterId !== encounterId) return false;
  if (n.unitId !== undefined && n.unitId === unit.unitId) return true;
  return !!unit.name && unit.name.trim().toLowerCase() === n.name.trim().toLowerCase();
}
